import * as React from "react";
import { Typography, Link, Stack } from "@mui/material";
import {
  useGetList,
  useRecordContext,
  TextField,
  DateField,
  Datagrid,
  FunctionField,
  useGetOne,
} from "react-admin";
import { AVA_ADMIN_URL } from "../../Routes";

export const ResultShow = () => {
  const record = useRecordContext();

  const getCompetencyName = (competency) =>
    competency?.competencyName ||
    competency?.name ||
    competency?.competency?.name ||
    competency?.competencyId ||
    "-";

  const getAvaRespondentId = (competency) => {
    if (!competency || typeof competency !== "object") return null;

    if (typeof competency.avaRespondentId === "string") {
      return competency.avaRespondentId;
    }

    if (
      competency.avaRespondentId &&
      typeof competency.avaRespondentId === "object"
    ) {
      return competency.avaRespondentId.id || competency.avaRespondentId.value || null;
    }

    if (typeof competency.respondentId === "string") return competency.respondentId;
    if (typeof competency.avaRespondentID === "string") return competency.avaRespondentID;
    if (typeof competency.avaId === "string") return competency.avaId;

    if (Array.isArray(competency.avaRespondentIds) && competency.avaRespondentIds.length > 0) {
      return competency.avaRespondentIds[0];
    }

    return (
      competency?.avaRespondent?.id ||
      competency?.respondent?.id ||
      competency?.metadata?.avaRespondentId ||
      null
    );
  };

  // Get employee results info
  const { data, isLoading, error } = useGetList("results/search", {
    filter: { ids: record.id },
  });

  const teamId = data?.length > 0 ? data[0].team.id : "";
  const employeeId = record.employeeId;

  // Get extended employee surveys results
  const hasSurveys = teamId && data[0]?.team?.surveys?.length > 0;
  const { data: surveysData } = useGetOne(
    "teams",
    {
      meta: {
        endpoint: `/teams/${teamId}/surveys`,
        queryParams: {
          employeeId: employeeId,
        },
        id: employeeId,
      },
    },
    {
      enabled: !!teamId && !!employeeId && hasSurveys,
    }
  );

  // Full employee info
  const fullInfo = React.useMemo(() => {
    const employeeResultsInfo = data && data.length > 0 ? data[0] : null;
    const fullInfo = {
      ...record,
      ...employeeResultsInfo,
    };

    // Competency results merge
    if (Array.isArray(record?.competencyResults)) {
      fullInfo.competencyResults = record.competencyResults.map((competency) => ({
        ...competency,
        ...(employeeResultsInfo?.competencyResults?.[competency.competencyId] || {}),
        competencyName: getCompetencyName({
          ...competency,
          ...(employeeResultsInfo?.competencyResults?.[competency.competencyId] || {}),
        }),
        avaRespondentId: getAvaRespondentId({
          ...competency,
          ...(employeeResultsInfo?.competencyResults?.[competency.competencyId] || {}),
        }),
      }));
    } else if (
      fullInfo?.competencyResults &&
      !Array.isArray(fullInfo.competencyResults) &&
      typeof fullInfo.competencyResults === "object"
    ) {
      fullInfo.competencyResults = Object.entries(fullInfo.competencyResults).map(
        ([competencyId, competency]) => {
          const fromDetails = employeeResultsInfo?.competencyResults?.[competencyId] || {};
          const mergedCompetency = {
            competencyId,
            ...(typeof competency === "object" && competency !== null
              ? competency
              : { score: competency }),
            ...fromDetails,
          };

          return {
            ...mergedCompetency,
            competencyName: getCompetencyName(mergedCompetency),
            avaRespondentId: getAvaRespondentId(mergedCompetency),
          };
        }
      );
    }

    // Get info of just one of the insight results (if any)
    if (
      fullInfo.insightResults &&
      Object.values(fullInfo.insightResults).length > 0
    ) {
      const singleInsightResult = Object.values(fullInfo.insightResults)[0];

      fullInfo.singleInsightResult = {
        status: singleInsightResult.status,
        endDateTime: singleInsightResult.endDateTime,
      };
    }

    return fullInfo;
  }, [data, record]);

  if (isLoading) return <span>Loading results...</span>;
  if (error) return <span>Error loading results</span>;

  return (
    <Stack direction="column" margin={1} spacing={3}>
      {fullInfo?.competencyResults?.length ? (
        <Stack direction="column">
          <Typography
            variant="h6"
            color="textSecondary"
            fontWeight="bold"
            mb={1}
          >
            Competency Result Breakdown
          </Typography>
          <Datagrid
            data={fullInfo.competencyResults}
            rowClick={false}
            bulkActionButtons={false}
          >
            <TextField source="competencyName" />
            <TextField source="score" />
            <TextField source="fit" />
            <FunctionField
              source="avaRespondentId"
              render={(record) =>
                getAvaRespondentId(record) ? (
                  <Link
                    href={`${AVA_ADMIN_URL}/respondents/show/${getAvaRespondentId(
                      record
                    )}`}
                    target="_blank"
                  >
                    AVA admin
                  </Link>
                ) : (
                  "-"
                )
              }
              label="AVA Admin Link"
            />
          </Datagrid>
        </Stack>
      ) : null}
      {surveysData?.overallSurveyResults ? (
        <Stack direction="column">
          <Typography
            variant="h6"
            color="textSecondary"
            fontWeight="bold"
            mb={1}
          >
            Surveys Result Breakdown
          </Typography>
          <Datagrid
            data={surveysData.overallSurveyResults}
            rowClick={false}
            bulkActionButtons={false}
          >
            <TextField source="type" label="Survey" />
            <TextField source="score" label="Score" />
            <TextField source="overallFit" label="Fit" />
            <DateField
              source="currentActiveRunStartDate"
              label="Current Active Run Start Date"
            />
          </Datagrid>
        </Stack>
      ) : null}
      {fullInfo?.singleInsightResult ? (
        <Stack direction="column">
          <Typography
            variant="h6"
            color="textSecondary"
            fontWeight="bold"
            mb={1}
          >
            Insights Result Breakdown
          </Typography>
          <Stack direction="column">
            <Stack direction="row" spacing={1}>
              <Typography variant="body2" color="textSecondary">
                Status:
              </Typography>
              <TextField
                record={fullInfo.singleInsightResult}
                source="status"
                emptyText=" - "
                variant="body2"
                color="textSecondary"
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography variant="body2" color="textSecondary">
                End date time:
              </Typography>
              <DateField
                record={fullInfo.singleInsightResult}
                source="endDateTime"
                color="textPrimary"
                emptyText=" - "
                variant="body2"
              />
            </Stack>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
};
